import {Storage} from "@/services/Storage";
import axios from "axios";
import router from "@/router";
import currencyModule from "@/store/modules/currency-module";
import connectivityMonitor from "@/services/connectivityMonitor";
import { generateUUID } from "@/utils/uuid";
import { hardwareBridge } from "@/services/hardwareBridge";

export default {
    name: 'accounts',
    state: {
        debtors : [],
        customer: '',
    },
    getters: {
        getDebtors: (state) =>state.debtors,
        getDebtorByID : (state) => (id) => {
            let customer = state.debtors.find(debtor => debtor.Id === id)
            if (customer){
                return customer.Name;
            }
            return id;
        },
        getCustomer: (state) => state.customer,
    },
    mutations: {
        setDebtors : (state , data) => state.debtors = data,
        addDebtorLocal: (state, debtor) => {
            // avoid duplicates by Id
            const exists = state.debtors.find(d => d.Id === debtor.Id);
            if (!exists) state.debtors.push(debtor);
        },
        replaceDebtorId: (state, { tempId, real }) => {
            const idx = state.debtors.findIndex(d => d.Id === tempId);
            if (idx !== -1) state.debtors.splice(idx, 1, real);
            if (state.customer === tempId) state.customer = real.Id;
        },
        setCustomer: (state, data) => {
            state.customer = data;
        },
    },
    actions: {
        setCustomer: (context, data) => {
            context.commit('setCustomer', data);
            if (!data) return;

            const debtor = context.state.debtors.find(deb => deb.Id === data);
            if (!debtor) return;

            const currency = context.state.currencyModule.storeCurrencies.find(c => c.Symbol === debtor.Symbol);
            if (currency) {
                context.commit('setSaleCurrency', currency);
            }
        },
        listOfflineDebtors: async (context) => {
            try {
                const list = await Storage.getOfflineCustomers();
                if (Array.isArray(list)) {
                    list.forEach(c => context.commit('addDebtorLocal', c));
                }
            } catch (e) {
                console.warn('Failed to load offline customers:', e);
            }
        },
        createOfflineDebtor: async (context, payload) => {
            try {
                const tempId = 'tmp_' + generateUUID();
                const offlineRecord = {
                    Id: tempId,
                    Name: payload.Name?.trim() || 'Customer',
                    Surname: payload.Surname?.trim() || '',
                    CellNumber: payload.CellNumber?.trim() || payload.contact?.trim() || '',
                    Adress1: payload.Adress1 || '',
                    Area: payload.Area || '',
                    vat: payload.vat || payload.tpin || '',
                    Balance: 0,
                    offline: true,
                    createdAt: new Date().toISOString(),
                };
                await Storage.addOfflineCustomer(offlineRecord);
                context.commit('addDebtorLocal', offlineRecord);
                // Return the created offline debtor
                return offlineRecord;
            } catch (e) {
                errorHandler.tomcatError(e, false, 'Failed to create offline customer');
                return null;
            }
        },
        syncOfflineDebtors: async (context) => {
            // Create any offline customers on the server, update mapping and local state
            const online = connectivityMonitor.getStatus();
            if (!online) return { synced: 0, failed: 0 };
            let store = await Storage.getDefaultStore();
            let list = await Storage.getOfflineCustomers();
            if (!Array.isArray(list) || list.length === 0) return { synced: 0, failed: 0 };

            let synced = 0, failed = 0;
            const remaining = [];
            for (const rec of list) {
                try {
                    // Build payload compatible with server createDebtor API
                    const payload = {
                        Name: rec.Name,
                        Surname: rec.Surname,
                        CellNumber: rec.CellNumber,
                        Adress1: rec.Adress1,
                        Area: rec.Area,
                        vat: rec.vat,
                    };
                    const { data } = await axios.post(serverApi + `debtor/${store.StoreDB}`, payload);
                    // Update mapping and local state
                    await Storage.updateCustomerIdMap(rec.Id, data.Id);
                    context.commit('replaceDebtorId', { tempId: rec.Id, real: data });
                    synced++;
                } catch (e) {
                    console.warn('Failed to sync offline customer:', rec, e);
                    remaining.push(rec);
                    failed++;
                }
            }
            await Storage.setOfflineCustomers(remaining);
            return { synced, failed };
        },
        resolveCustomerId: async (context, id) => {
            if (!id || typeof id !== 'string' || !id.startsWith('tmp_')) return id;
            const map = await Storage.getCustomerIdMap();
            return map[id] || null;
        },
        getAllDebtors : async (context,payload) => {
            let store = await Storage.getDefaultStore();
            payload = {
                ...payload,
                blocked: false
            }
            return axios.post(franchiseTomcatApi + `debtor/${store.StoreDB}/search`,payload)
                .then(({data})=> context.commit('setDebtors', data))
                .catch((err) => errorHandler.tomcatError(err))
        },
        createDebtor : async (context , payload) => {
            let store = await Storage.getDefaultStore();
            try {
                // Ticket #26272: client format gate for TPIN (vat). Empty allowed; uniqueness is server-side.
                const tpin = String(payload.vat || payload.tpin || '').trim();
                if (tpin && !/^\d{10}$/.test(tpin)) {
                    const msg = 'TPIN must be 10 digits (e.g., 1234567890).';
                    responses.throwErr(msg);
                    throw new Error(msg);
                }
                payload = { ...payload, vat: tpin };

                // Check SAP integration status
                let isSap = false;
                try {
                    const { data } = await axios.get(`${subscriptionApi}integrations/checkSapRegistration/${store.StoreDB}`);
                    isSap = !!(data?.registered ?? data?.isSap ?? data);
                } catch (e) {
                    // Non-blocking: if check fails, proceed without setting isCash
                    console.warn('SAP integration check failed:', e);
                }

                // If integrated with SAP, enforce cash debtor
                if (isSap) {
                    payload = { ...payload, isCash: true };
                }

                const response = await axios.post(serverApi + `debtor/${store.StoreDB}`, payload);
                await context.dispatch(`getAllDebtors`,{
                    Name: "",
                    tpin: "",
                    contact: ""
                });
                return response.data;
            } catch (err) {
                return errorHandler.tomcatError(err)
            }
        },

        printDebtorStatement : async (context,id) => {
            let store = await Storage.getDefaultStore();
            return axios.get(franchiseTomcatApi + `getStatement/${store.StoreDB}?ID=${id}`,)
                .then(({data})=> {
                    try {
                        Printing.printStatement(JSON.stringify(data))
                    }catch (e) {}
                }).catch((err) => errorHandler.tomcatError(err))
        },
        makeDebtorPayment: async (context, payload) => {
            let store = await Storage.getDefaultStore();
            let user = await Storage.getCurrentUser();
            payload.User = user.Username;

            return axios.post(backOfficeTomcatApi+`debtor/${store.StoreDB}/makeCashierPayment` , payload)
                .then((response) => {
                    try {
                    }catch (e) {}
                    context.commit(`setCustomer`,'')
                    responses.showSuccess(`Payment Successfully Made`)

                    // Return payment ID for receipt printing
                    const paymentId = response.data?.id || response.data?.paymentId || response.data?.Id;
                    return { success: true, paymentId: paymentId };
                }).catch((err) => errorHandler.tomcatError(err,true))
        },

        printPaymentReceipt: async (context, paymentId) => {
            if (!paymentId) {
                responses.showWarning('Payment ID not found. Cannot print receipt.');
                return;
            }

            let store = await Storage.getDefaultStore();
            let settings = await Storage.getDeviceSettings();
            const pcName = settings?.PC || 'POS-01';

            try {
                // Call the print service API to get base64 data
                const response = await axios.post(window.printVoid + `printing/${store.StoreDB}/printPaymentReceipt/${paymentId}?PC=${pcName}`, settings);

                // Handle printing based on platform and settings
                if (window.androidMode) {
                    return window.location.href = `rawbt:base64,${response.data}`;
                }

                if (settings.Printer) {
                    // Prefer Electron IPC when available
                    if (window?.hardwareIPC || window?.electronBridge) {
                        const ok = await hardwareBridge.printBase64({
                            printer: settings.Printer,
                            base64: response.data
                        });
                        if (!ok) return responses.showWarning('Failed to send print to Electron printer');
                        return true;
                    }
                    // Fallback to Local Hardware Service
                    let payload = {
                        printer: settings.Printer,
                        base64: response.data
                    };
                    return axios.post(window.hardwareApi + 'printing/base64', payload);
                } else {
                    return responses.showWarning('No printer selected');
                }
            } catch (err) {
                errorHandler.tomcatError(err, false, `Payment Receipt Print Error`);
            }
        },

        firstKazangAccountPaymentStep : async (context,payload) => {
            let store = await Storage.getDefaultStore();
            let settings = await Storage.getDeviceSettings();
            const amount = Number(parseFloat(payload.PayedAmount).toFixed(2));

            // Validate amount is greater than 0
            if (amount <= 0) {
                throw new Error('Amount must be greater than 0.');
            }

            const msisdn = payload.customerContact;
            const network = await context.dispatch('checkCustomerNetwork', msisdn);
            const localid = `kaz-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
            const pc = encodeURIComponent(settings.PC || 'Online');
            const url = `${serverApi}api/payments/kazang/${store.StoreDB}?pc=${pc}`;

            return axios.post(url, {
                localid,
                amount,
                msisdn,
                network
            }).then(({data}) => {
                const reference = data && (data.LocalID || data.localid) ? (data.LocalID || data.localid) : localid;
                if (!data.secondStep && data.Response === '0') {
                    return context.dispatch('makeDebtorPayment', payload)
                } else if (data.Response === '3') {
                    return context.dispatch(`resetKazangUser`, data.Response);
                } else if (data.Response === '41') {
                    return context.dispatch(`resetKazangUser`, data.Response);
                } else if (data.Response !== '0') {
                    throw new Error(data.Print)
                } else {
                    context.commit('setKazangSession', data.newSession)
                    return { SecondRefrence: reference, ...data };
                }
            }).catch((err) => {
                context.commit('clearKazangSession')
                errorHandler.tomcatError(err, true)
            })
        },

        secondKazangAccountPaymentStep : async (context,firstStepResponse) => {
            let store = await Storage.getDefaultStore();
            let network = await context.dispatch('checkCustomerNetwork', firstStepResponse.customerContact);
            let route = ''

            switch (network) {
                case 'MTN':
                    route = '/mtnApprove';
                    break
                case 'Airtel':
                    route = '/airtelApprove';
                    break
                default:
                    route = '/zamtelApprove'
                    break
            }
            let url = kazangApi + `pay/${store.StoreDB}${route}`;
            return axios.post(url, JSON.parse(firstStepResponse.SecondStepPayload))
                .then(({data}) => {
                    if (!data.Error) {
                        if (!data.approved) {
                            return {
                                Error: 'Not Confirmed'
                            }
                        }
                        return context.dispatch('makeDebtorPayment', firstStepResponse.paymentData)
                    }
                    return data
                }).catch((err) => {
                    context.commit('clearKazangSession')
                    errorHandler.tomcatError(err)
                })
        },
        getCustomerById:async (context,id)=>{
            let store = await Storage.getDefaultStore();
            return axios.get(serverApi+`debtor/${store.StoreDB}/${id}`)
                .then(({data})=>context.state.debtors.push(data))
        },
    },
    modules : { currencyModule }
}
